import React, { useMemo } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FONT_FAMILY } from '../common/sharedFunctions';
import eatsLogo from '../assets/img/eatslogo.png';
import { EAT_GREEN } from '../common/eatBrand';

const GOLD = '#d4af37';
const WA_NUMBER = '33669675282';
const makeWALink = (text) => `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;

const LandingActionPanel = React.memo(() => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = useMemo(() => {
    const rawLang = i18n?.language || i18n?.languages?.[0] || 'fr';
    return rawLang.toLowerCase().startsWith('fr') ? 'fr' : 'en';
  }, [i18n?.language, i18n?.languages]);

  const content = lang === 'fr' ? {
    eyebrow: 'Plats à emporter Toulouse · Montauban · Blagnac',
    title: 'Vos plats à emporter livrés en 20-30 min ou VTC immédiat',
    subtitle: 'Commandez votre chauffeur privé ou faites-vous livrer vos plats à emporter préférés à Toulouse, Montauban, Blagnac et toute l\'agglomération. Inscription ouverte pour les livreurs et restaurateurs locaux.',
    actions: [
      {
        title: 'Réserver un VTC',
        text: 'Chauffeur privé disponible immédiatement à Toulouse, Montauban, Blagnac et alentours. Prix fixe, réservation en 30 secondes.',
        label: 'Réserver ma course',
        path: '/reservation',
        color: GOLD,
        icon: <LocalTaxiIcon />
      },
      {
        title: 'Plats à emporter à domicile',
        text: 'Vos restaurants préférés de Toulouse, Montauban et Blagnac livrés chez vous en 20-30 min. Sushi, burgers, pizzas, cuisine locale... Commandez via WhatsApp ou l\'app.',
        label: 'Commander un plat',
        whatsappPrompt: lang === 'fr' ? 'Bonjour, j\'aimerais commander un plat à emporter.' : 'Hello, I would like to order a takeaway.',
        color: EAT_GREEN,
        image: eatsLogo
      },
      {
        title: 'Devenir restaurateur partenaire',
        text: 'Augmentez vos ventes avec notre service de livraison. Couverture Toulouse, Montauban, Blagnac et toute l\'agglomération. Inscription gratuite.',
        label: 'Devenir partenaire',
        path: '/restaurants',
        color: GOLD,
        icon: <StorefrontIcon />
      }
    ]
  } : {
    eyebrow: 'Quick access',
    title: 'Choose your Allô Driver flow',
    subtitle: 'VTC ride, Eats order or restaurant access: each need has a dedicated entry point.',
    actions: [
      {
        title: 'Book a ride',
        text: 'Enter your route and choose your vehicle.',
        label: 'Book',
        path: '/reservation',
        color: GOLD,
        icon: <LocalTaxiIcon />
      },
      {
        title: 'Order food',
        text: 'Partner restaurants are available from the app.',
        label: 'Get the app',
        anchor: 'app-download',
        color: EAT_GREEN,
        image: eatsLogo
      },
      {
        title: 'Restaurant partner',
        text: 'Request one dedicated access for one restaurant.',
        label: 'Request access',
        path: '/restaurants',
        color: GOLD,
        icon: <StorefrontIcon />
      }
    ]
  };

  const goToAction = (action) => {
    if (action.whatsappPrompt) {
      openGaelle(action.whatsappPrompt);
      return;
    }
    if (action.anchor) {
      const target = document.getElementById(action.anchor);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    navigate(action.path);
  };

  const openGaelle = (prompt) => {
    const link = makeWALink(prompt);
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 4, md: 6 }, backgroundColor: '#050505' }}>
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
          <Typography sx={{ color: EAT_GREEN, fontFamily: FONT_FAMILY, fontWeight: 900, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0, mb: 1 }}>
            {content.eyebrow}
          </Typography>
          <Typography component="h2" sx={{ color: '#fff', fontFamily: FONT_FAMILY, fontWeight: 900, fontSize: { xs: 28, md: 42 }, lineHeight: 1.08, letterSpacing: 0, mb: 1.5 }}>
            {content.title}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,.68)', fontFamily: FONT_FAMILY, fontSize: { xs: 15, md: 17 }, lineHeight: 1.55, maxWidth: 720, mx: 'auto' }}>
            {content.subtitle}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {content.actions.map((action) => (
            <Grid item xs={12} md={4} key={action.title}>
              <Box sx={{ height: '100%', minHeight: 216, p: { xs: 2.2, md: 2.6 }, borderRadius: '20px', backgroundColor: 'rgba(255,255,255,.055)', border: `1px solid ${action.color}44`, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 54, height: 54, borderRadius: '16px', backgroundColor: `${action.color}22`, border: `1px solid ${action.color}66`, display: 'grid', placeItems: 'center', color: action.color }}>
                    {action.image ? <Box component="img" src={action.image} alt="Allô Driver Eats" sx={{ width: 42, height: 42, borderRadius: '10px' }} /> : action.icon}
                  </Box>
                  <Typography sx={{ color: '#fff', fontFamily: FONT_FAMILY, fontWeight: 900, fontSize: 20, lineHeight: 1.15 }}>
                    {action.title}
                  </Typography>
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,.66)', fontFamily: FONT_FAMILY, fontSize: 15, lineHeight: 1.5 }}>
                  {action.text}
                </Typography>
                <Button onClick={() => goToAction(action)} endIcon={<ArrowForwardIcon />} sx={{ alignSelf: 'flex-start', color: '#050505', backgroundColor: action.color, borderRadius: '12px', px: 2.2, py: 1.1, textTransform: 'none', fontFamily: FONT_FAMILY, fontWeight: 900, '&:hover': { backgroundColor: action.color } }}>
                  {action.label}
                </Button>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 2, p: { xs: 2, md: 2.5 }, borderRadius: '20px', backgroundColor: 'rgba(212,175,55,.08)', border: '1px solid rgba(212,175,55,.24)', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography sx={{ color: '#fff', fontFamily: FONT_FAMILY, fontWeight: 900, fontSize: { xs: 18, md: 22 }, mb: .5 }}>Gaëlle vous guide</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,.66)', fontFamily: FONT_FAMILY, fontSize: 14, lineHeight: 1.45 }}>
              {lang === 'fr' ? 'Inscriptions, documents, livraison, restauration ou SAV : posez votre question au bot Allô Driver.' : 'Registration, documents, delivery, restaurants or support: ask the Allô Driver bot.'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button onClick={() => openGaelle('Je veux devenir chauffeur VTC, quels documents fournir ?')} sx={{ color: '#050505', backgroundColor: GOLD, borderRadius: '12px', textTransform: 'none', fontWeight: 900, '&:hover': { backgroundColor: GOLD } }}>Chauffeur VTC</Button>
            <Button onClick={() => openGaelle('Je veux devenir livreur Eats, comment faire mon inscription ?')} sx={{ color: '#04140c', backgroundColor: EAT_GREEN, borderRadius: '12px', textTransform: 'none', fontWeight: 900, '&:hover': { backgroundColor: EAT_GREEN } }}>Livreur Eats</Button>
            <Button onClick={() => openGaelle('Je suis restaurateur, comment demander un accès Allô Driver Eats ?')} sx={{ color: GOLD, border: `1px solid ${GOLD}`, borderRadius: '12px', textTransform: 'none', fontWeight: 900 }}>Restaurateur</Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

export default LandingActionPanel;